import { Box, Typography } from "@mui/material";
import { FlexColBox } from "../../../../common/components/Box";
import { Trans, useTranslation } from "react-i18next";
import { Chip, Radio } from "../../../../common/components";
import { useEffect, useState } from "react";
import hangTagsImg from "../../../../assets/images/hang_tag.jpg";
import individualPolyBagsImg from "../../../../assets/images/individual-poly-bags.png";
import { palette, spacing } from "../../../../styles/theme";
import CartService from "../../../../service/CartService";
import { useCart } from "../../../../utils/CartHooks";
import { FactoryId } from "../../../../types";

interface Props {
  hangTags: boolean;
  individualPolyBags: boolean;
  factoryId: FactoryId;
}
const CartItemAddtionalOptions = ({
  hangTags,
  individualPolyBags,
  factoryId,
}: Props) => {
  const { t } = useTranslation();
  const { fetchCartItemsAndUpdateStore } = useCart();
  const [isHangTagsSelected, setIsHangTagsSelected] = useState(
    hangTags ?? false
  );
  const [isIndividualPolyBagsSelected, setIsIndividualPolyBagsSelected] =
    useState(individualPolyBags ?? false);

  const [activeOptions, setActiveOptions] = useState<
    "individual_poly_bags" | "hang_tags"
  >("hang_tags");

  const handleHangTagsChange = async (checked: boolean) => {
    setIsHangTagsSelected(checked);
    await CartService.updateAdditionalOptions(factoryId, "hangTags", checked);
    fetchCartItemsAndUpdateStore();
  };

  const handleIndividualPolyBagsChange = async (checked: boolean) => {
    setIsIndividualPolyBagsSelected(checked);
    await CartService.updateAdditionalOptions(
      factoryId,
      "individualPolyBags",
      checked
    );
    fetchCartItemsAndUpdateStore();
  };

  useEffect(() => {
    setIsHangTagsSelected(hangTags ?? false);
    setIsIndividualPolyBagsSelected(individualPolyBags ?? false);
  }, [hangTags, individualPolyBags]);

  if (factoryId !== "RI") {
    return null;
  }

  return (
    <FlexColBox gap={2}>
      <Typography variant="textRegularBold">
        {t("checkout.additional_options")}
      </Typography>
      <Box display="flex" gap={1} alignItems="center">
        <Chip
          label={t("checkout.hang_tags.title")}
          active={activeOptions === "hang_tags"}
          onClick={() => setActiveOptions("hang_tags")}
        />
        <Chip
          label={t("checkout.individual_poly_bags.title")}
          active={activeOptions === "individual_poly_bags"}
          onClick={() => setActiveOptions("individual_poly_bags")}
        />
      </Box>
      <Box
        display="flex"
        gap={{ xs: spacing.groupingMobile, lg: spacing.groupingDesktop }}
      >
        <img
          src={
            activeOptions === "hang_tags" ? hangTagsImg : individualPolyBagsImg
          }
          alt={
            activeOptions === "hang_tags"
              ? t("checkout.hang_tags.title")
              : t("checkout.individual_poly_bags.title")
          }
          width={109}
          height={109}
          style={{ borderRadius: "4px" }}
        />
        <FlexColBox
          gap={{ xs: spacing.mediumMobile, lg: spacing.mediumDesktop }}
        >
          <FlexColBox gap={0.5}>
            <Typography variant="textRegular">
              {t(`checkout.${activeOptions}.description`)}
            </Typography>
            {activeOptions === "hang_tags" && (
              <Typography
                variant="textSm"
                color={palette.misc.green}
                fontStyle="italic"
              >
                {t(`checkout.${activeOptions}.sub_description`)}
              </Typography>
            )}
          </FlexColBox>
          <Radio
            label={
              <Typography variant="textRegular">
                <Trans
                  i18nKey={`checkout.${activeOptions}.yes_option`}
                  components={[
                    <Typography
                      variant="textRegular"
                      color={palette.misc.green}
                      fontSize={12}
                    />,
                  ]}
                />
              </Typography>
            }
            checked={
              activeOptions === "hang_tags"
                ? isHangTagsSelected
                : isIndividualPolyBagsSelected
            }
            onChange={
              activeOptions === "hang_tags"
                ? (event) => handleHangTagsChange(true)
                : (event) => handleIndividualPolyBagsChange(true)
            }
          />
          <Radio
            label={
              <Typography variant="textRegular">
                {t(`checkout.${activeOptions}.no_thanks`)}
              </Typography>
            }
            checked={
              !(activeOptions === "hang_tags"
                ? isHangTagsSelected
                : isIndividualPolyBagsSelected)
            }
            onChange={
              activeOptions === "hang_tags"
                ? (event) => handleHangTagsChange(false)
                : (event) => handleIndividualPolyBagsChange(false)
            }
          />
        </FlexColBox>
      </Box>
    </FlexColBox>
  );
};

export default CartItemAddtionalOptions;
